import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { 
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { toast } from 'sonner';
import { 
  Clock, 
  Star, 
  MapPin, 
  Phone,
  Heart,
  Loader2,
  Undo2,
  Utensils,
  Car,
  Scissors,
  Wrench,
  Home,
  Briefcase,
  TreeDeciduous,
  Coffee,
  ShoppingBag,
  Stethoscope,
  GraduationCap,
  Dumbbell,
  PawPrint,
  Landmark,
  Store,
  Building2
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category icon mapping
const categoryIcons = {
  restaurant: Utensils,
  food: Utensils,
  cafe: Coffee,
  coffee: Coffee,
  car_repair: Car,
  car_dealer: Car,
  car_wash: Car,
  beauty_salon: Scissors,
  hair_care: Scissors,
  spa: Heart,
  plumber: Wrench,
  electrician: Wrench,
  contractor: Wrench,
  real_estate_agency: Home,
  real_estate: Home,
  lawyer: Briefcase,
  accounting: Briefcase,
  insurance_agency: Briefcase,
  doctor: Stethoscope,
  dentist: Stethoscope,
  hospital: Stethoscope,
  pharmacy: Stethoscope,
  veterinary_care: PawPrint,
  pet_store: PawPrint,
  gym: Dumbbell,
  fitness: Dumbbell,
  school: GraduationCap,
  university: GraduationCap,
  bank: Landmark,
  landscaping: TreeDeciduous,
  garden: TreeDeciduous,
  store: Store,
  shopping: ShoppingBag,
  default: Building2
};

const formatCategory = (category) => {
  if (!category) return 'Business';
  return category
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
};

const formatAddress = (address) => {
  if (!address) return '';
  let result = address.replace(
    /\b([A-Za-z]{1,2}[0-9][0-9A-Za-z]?\s*[0-9][A-Za-z]{2})\b/gi,
    (match) => match.toUpperCase()
  );
  result = result
    .split(' ')
    .map(word => {
      if (word === word.toUpperCase() && word.length <= 4) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
  return result
    .replace(/\bUk\b/g, 'UK')
    .replace(/\bNw(\d)/gi, 'NW$1')
    .replace(/\bSw(\d)/gi, 'SW$1')
    .replace(/\bSe(\d)/gi, 'SE$1')
    .replace(/\bNe(\d)/gi, 'NE$1')
    .replace(/\bEc(\d)/gi, 'EC$1')
    .replace(/\bWc(\d)/gi, 'WC$1');
};

const getCategoryIcon = (category) => {
  if (!category) return categoryIcons.default;
  const lowerCategory = category.toLowerCase();
  for (const [key, icon] of Object.entries(categoryIcons)) {
    if (lowerCategory.includes(key)) {
      return icon;
    }
  }
  return categoryIcons.default;
};

const SnoozedBusinesses = () => {
  const { token } = useAuth();
  const [snoozed, setSnoozed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unsnoozing, setUnsnoozing] = useState(null);
  const [addingFavourite, setAddingFavourite] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchSnoozed();
  }, []);

  const fetchSnoozed = async () => {
    try {
      const response = await axios.get(`${API}/snoozed-businesses`, { headers });
      setSnoozed(response.data);
    } catch (error) {
      console.error('Failed to fetch snoozed:', error);
      toast.error('Failed to load snoozed businesses');
    } finally {
      setLoading(false);
    }
  };

  const handleUnsnooze = async (business) => {
    setUnsnoozing(business.business_id);
    try {
      await axios.delete(`${API}/snoozed-businesses/${business.business_id}`, { headers });
      toast.success(`${business.business_name} unsnoozed`);
      setSnoozed(prev => prev.filter(s => s.business_id !== business.business_id));
    } catch (error) {
      toast.error('Failed to unsnooze business');
    } finally {
      setUnsnoozing(null);
    }
  };

  const handleAddToFavourites = async (business) => {
    setAddingFavourite(business.business_id);
    try {
      await axios.post(`${API}/favourites`, {
        id: business.business_id,
        name: business.business_name,
        address: business.address || '',
        phone: business.phone || '',
        rating: business.rating || 0,
        review_count: business.review_count || 0,
        category: business.category || '',
        postcode: business.postcode || ''
      }, { headers });
      
      // Remove from snoozed list
      await axios.delete(`${API}/snoozed-businesses/${business.business_id}`, { headers });
      
      toast.success(`${business.business_name} added to favourites!`);
      setSnoozed(prev => prev.filter(s => s.business_id !== business.business_id));
    } catch (error) {
      toast.error('Failed to add to favourites');
    } finally {
      setAddingFavourite(null);
    }
  };

  const formatTimeRemaining = (snoozedUntil) => {
    const until = new Date(snoozedUntil);
    const now = new Date();
    const days = Math.ceil((until - now) / (1000 * 60 * 60 * 24));
    if (days <= 0) return 'Expired';
    if (days === 1) return '1 day left';
    return `${days} days left`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="snoozed-businesses">
      <Card className="bg-card/50 border-white/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            Snoozed Businesses ({snoozed.length})
          </CardTitle>
          <CardDescription>
            Businesses you&apos;ve snoozed will reappear in search after 30 days. You can unsnooze or add to favourites anytime.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {snoozed.length === 0 ? (
            <div className="py-16 text-center">
              <Clock className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium mb-2">No snoozed businesses</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                When you click &quot;Not Yet&quot; on a business in search results, it will appear here.
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {snoozed.map((business) => {
                const CategoryIcon = getCategoryIcon(business.category);
                
                return (
                  <div
                    key={business.business_id}
                    className="bg-background/50 border border-white/5 rounded-xl p-5 flex flex-col md:flex-row md:items-center gap-4 transition-all hover:border-white/10"
                    data-testid={`snoozed-${business.business_id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <CategoryIcon className="w-6 h-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-lg">{business.business_name}</h4>
                          {business.address && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                              <MapPin className="w-3 h-3" /> {formatAddress(business.address)}
                            </p>
                          )}
                          {business.phone && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
                              <Phone className="w-3 h-3" /> {business.phone}
                            </p>
                          )}
                          <div className="flex items-center gap-4 mt-2">
                            {business.rating > 0 && (
                              <span className="text-sm flex items-center gap-1">
                                <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                                <span className="font-medium">{business.rating}</span>
                                {business.review_count > 0 && (
                                  <span className="text-muted-foreground">({business.review_count} reviews)</span>
                                )}
                              </span>
                            )}
                            {business.category && (
                              <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                                {formatCategory(business.category)}
                              </span>
                            )}
                            <span className="text-xs px-2 py-1 rounded-full bg-orange-500/10 text-orange-500 font-medium">
                              <Clock className="w-3 h-3 inline mr-1" />
                              {formatTimeRemaining(business.snoozed_until)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleAddToFavourites(business)}
                        disabled={addingFavourite === business.business_id}
                        className="gap-2"
                        data-testid={`favourite-${business.business_id}`}
                      >
                        {addingFavourite === business.business_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Heart className="w-4 h-4" />
                            Add to Favourites
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleUnsnooze(business)}
                        disabled={unsnoozing === business.business_id}
                        variant="outline"
                        className="gap-2"
                        data-testid={`unsnooze-${business.business_id}`}
                      >
                        {unsnoozing === business.business_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Undo2 className="w-4 h-4" />
                            Unsnooze
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SnoozedBusinesses;
